import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";

function notificationTitle(row) {
  const data = row?.data || {};
  if (data.title) return data.title;
  return "Notification";
}

function notificationMessage(row) {
  const data = row?.data || {};
  return data.message || "";
}

function isClassNotification(row) {
  return String(row?.data?.type || "").startsWith("class_");
}

function notificationTypeLabel(row) {
  const type = row?.type || "";
  const data = row?.data || {};
  if (data.category === "billing" || type.includes("OneTimePurchaseReceipt")) return "Billing";
  if (type.includes("MobileParentSubscription") || type.includes("MobileSubscription")) return "Plan";
  if (type.includes("Announcement")) return "Announcement";
  if (isClassNotification(row)) return "Class";
  return "Update";
}

/**
 * Parent / student in-app inbox. Same API shape as tutor/notifications.
 */
export default function PanelNotifications({
  apiPrefix,
  subtitle = "Payment receipts and other updates for this account.",
}) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [marking, setMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get(`${apiPrefix}/notifications`, { params: { per_page: 50 } });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setUnreadCount(res.data?.meta?.unread_count ?? 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [apiPrefix]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await api.post(`${apiPrefix}/notifications/${id}/mark-as-read`);
      await fetchNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to mark notification as read.");
    }
  };

  const markAllRead = async () => {
    setMarking(true);
    try {
      await api.post(`${apiPrefix}/notifications/mark-all-as-read`);
      toast.success("All notifications marked as read.");
      await fetchNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to mark all as read.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        {unreadCount > 0 ? (
          <Button variant="outlined" size="small" onClick={markAllRead} disabled={marking}>
            {marking ? "Marking…" : "Mark all read"}
          </Button>
        ) : null}
      </Box>

      <Paper sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, px: 2, textAlign: "center" }}>
            No notifications yet.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((row, index) => {
              const unread = !row.read_at;
              return (
                <React.Fragment key={row.id}>
                  {index > 0 ? <Divider /> : null}
                  <ListItem
                    alignItems="flex-start"
                    secondaryAction={
                      unread ? (
                        <Button size="small" onClick={() => markRead(row.id)}>
                          Mark read
                        </Button>
                      ) : (
                        <Chip size="small" label="Read" variant="outlined" />
                      )
                    }
                    sx={{
                      py: 2,
                      px: 2,
                      bgcolor: unread ? "rgba(59, 42, 159, 0.04)" : "transparent",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 10 }}>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {notificationTitle(row)}
                          </Typography>
                          <Chip size="small" label={notificationTypeLabel(row)} variant="outlined" />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                            {notificationMessage(row)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                            {row.created_at_human || row.created_at}
                          </Typography>
                          {isClassNotification(row) ? (
                            <Button component={RouterLink} to={`/${apiPrefix}/classes`} size="small" sx={{ mt: 0.5, px: 0 }}>
                              View classes
                            </Button>
                          ) : null}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>
    </Box>
  );
}
